/**
 * MarkdownEditor — true WYSIWYG editor (TipTap / ProseMirror).
 *
 * Sprint 3 (S3.D) shipped a split-mode markdown editor (`@uiw/react-md-editor`)
 * that exposed both source markdown and live preview side-by-side.
 * Recruiters are not power users — most never realised the left pane was
 * editable and confused themselves typing into the preview. The
 * follow-up (20/05/26) replaces it with a true WYSIWYG: the recruiter
 * sees and edits the rendered output, never the markdown source.
 *
 * Storage stays on markdown (BE persists position descriptions as
 * markdown for screening prompts + the public landing page). The
 * `tiptap-markdown` extension parses markdown on `setContent` and
 * serialises back on every change so callers keep the same
 * `value: string / onChange: (md: string) => void` contract from before.
 *
 * Toolbar is minimal but covers the formatting recruiters actually use:
 * bold, italic, headings (H1/H2/H3), bullet/ordered lists, link, quote,
 * code, undo/redo. `Markdown` paste is supported transparently.
 */

import * as React from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';
import {
  Bold, Italic, List, ListOrdered, Quote, Heading2, Heading3,
  Link as LinkIcon, Code, Undo2, Redo2,
} from 'lucide-react';

import { cn } from '@/lib/utils';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Editor minimum visible height in px. Default 280 — bigger than the
   *  legacy 220 because users complained the editing area felt cramped. */
  minHeight?: number;
  className?: string;
  ariaLabel?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Toolbar — single source of truth for the formatting commands. Keeps the
// callsite simple (`<MarkdownEditor value onChange />`) without exposing
// TipTap's instance to the parent.
// ─────────────────────────────────────────────────────────────────────────────

interface ToolbarButtonProps {
  editor: Editor;
  command: () => void;
  active?: boolean;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}
function ToolbarButton({ command, active, disabled, label, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={command}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-foreground',
        active && 'bg-accent text-foreground',
        disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
      )}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const promptForLink = React.useCallback(() => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previousUrl ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank', rel: 'noopener noreferrer' }).run();
  }, [editor]);

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-input bg-muted/30">
      <ToolbarButton editor={editor} command={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} label="Bold (⌘B)">
        <Bold className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton editor={editor} command={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} label="Italic (⌘I)">
        <Italic className="h-3.5 w-3.5" />
      </ToolbarButton>
      <span className="mx-1 h-4 w-px bg-border" aria-hidden />
      <ToolbarButton editor={editor} command={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} label="Heading 2">
        <Heading2 className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton editor={editor} command={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} label="Heading 3">
        <Heading3 className="h-3.5 w-3.5" />
      </ToolbarButton>
      <span className="mx-1 h-4 w-px bg-border" aria-hidden />
      <ToolbarButton editor={editor} command={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} label="Bullet list">
        <List className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton editor={editor} command={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} label="Ordered list">
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton editor={editor} command={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} label="Quote">
        <Quote className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton editor={editor} command={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} label="Inline code">
        <Code className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton editor={editor} command={promptForLink} active={editor.isActive('link')} label="Link">
        <LinkIcon className="h-3.5 w-3.5" />
      </ToolbarButton>
      <span className="mx-1 h-4 w-px bg-border" aria-hidden />
      <ToolbarButton
        editor={editor}
        command={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        label="Undo"
      >
        <Undo2 className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        editor={editor}
        command={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        label="Redo"
      >
        <Redo2 className="h-3.5 w-3.5" />
      </ToolbarButton>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  minHeight = 280,
  className,
  ariaLabel,
}: MarkdownEditorProps): JSX.Element {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // We let `tiptap-markdown` own the markdown ↔ HTML conversion. The
        // StarterKit's default schema is fine; the markdown extension hooks
        // into it.
      }),
      Link.configure({
        openOnClick: false, // editing flow — click to position caret, not open
        autolink: true,
        protocols: ['https', 'http', 'mailto'],
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Placeholder.configure({
        placeholder: placeholder ?? '',
        showOnlyWhenEditable: true,
      }),
      Markdown.configure({
        // `html: false` keeps the storage contract pure-markdown — pasting
        // raw HTML gets sanitised to its markdown equivalent.
        html: false,
        tightLists: true,
        linkify: true,
        breaks: true,
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        'aria-label': ariaLabel ?? 'Editor',
        class: 'prose prose-sm max-w-none px-3 py-2 focus:outline-none',
      },
    },
    onUpdate: ({ editor: ed }) => {
      // `tiptap-markdown` adds storage.markdown on the editor. Use it to
      // serialise the current document back to markdown before bubbling.
      const md = (ed.storage as { markdown?: { getMarkdown: () => string } }).markdown
        ?.getMarkdown() ?? ed.getText();
      onChange(md);
    },
  });

  // Keep editor in sync with controlled `value` when callers reset it
  // externally (e.g. AI auto-fill applying suggestions, dialog reopen
  // clearing the form). We compare against the current markdown to avoid
  // losing the caret on every keystroke.
  React.useEffect(() => {
    if (!editor) return;
    const currentMd =
      (editor.storage as { markdown?: { getMarkdown: () => string } }).markdown
        ?.getMarkdown() ?? editor.getText();
    if (value !== currentMd) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [editor, value]);

  return (
    <div
      className={cn(
        'rounded-md border border-input bg-background overflow-hidden',
        'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background',
        className,
      )}
    >
      {editor && <Toolbar editor={editor} />}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none"
        style={{ minHeight: `${minHeight}px` }}
      />
    </div>
  );
}

export default MarkdownEditor;
